import * as queryString from 'query-string';
import {capitalizeFirst} from '../string';
import {matchPath} from 'react-router';
import history from '../history';
import {GALLERY_PATH} from '../../routeData';
import {useGalleryTagSectionsQuery} from '../../generated/graphql';
import {EMPTY_ARRAY} from '../constants';
import {useEffect, useMemo, useRef} from 'react';
import produce from 'immer';
import {
  ExtraStuff,
  FeaturedReport,
  GalleryTag,
  GalleryTagSection,
} from './types';
import {useDispatch, useSelector} from '../../state/hooks';
import {setGalleryTagSections} from '../../state/global/actions';
import {useViewer} from '../../state/viewer/hooks';

export * from './types';

export const TOP_CONTRIBUTORS_LABEL = 'Top contributors';

const GALLERY_ADMINS = new Set([
  'axel@wandb.com',
  'lavanya@wandb.com',
  'angelica@wandb.com',
  'ivan@wandb.com',
  'justin.tenuto@wandb.com',
  'carey@wandb.com',
]);

export function useIsGalleryAdmin(): boolean {
  const viewer = useViewer();
  return GALLERY_ADMINS.has(viewer?.email ?? '');
}

export function newGalleryTag(): GalleryTag {
  return {
    name: '',
    description: '',
    linkForm: '',
  };
}

export function newGalleryTagSection(): GalleryTagSection {
  return {
    name: '',
    description: '',
    tags: [],
  };
}

export function newExtraStuff(): ExtraStuff {
  return {
    name: '',
    link: '',
    time: '',
  };
}

export function useTranslationLanguages(): string[] {
  const tagSections = useGalleryTagSections();
  const inSec = tagSections.find(sec => sec.name === 'International');
  return inSec?.tags.map(t => t.name) ?? [];
}

export function sectionFromTag(
  tagSections: readonly GalleryTagSection[],
  tag: string
): string | null {
  const formatted = formatGalleryTag(tagSections, tag);
  return sectionFromFormattedTag(tagSections, formatted);
}

export function sectionFromFormattedTag(
  tagSections: readonly GalleryTagSection[],
  tag: string
): string | null {
  for (const {name: sectionName, tags} of tagSections) {
    for (const {name: tagName, subtags = []} of tags) {
      if (tagName === tag) {
        return sectionName;
      }
      for (const {name: subtagName} of subtags) {
        if (subtagName === tag) {
          return sectionName;
        }
      }
    }
  }
  return null;
}

export function galleryTagFromQS(): string | null {
  const qs = queryString.parse(window.location.search);
  return (qs.galleryTag as string) ?? null;
}

export function useFormatGalleryTag(linkForm: string): string {
  const tagSections = useGalleryTagSections();
  return formatGalleryTag(tagSections, linkForm);
}

export function formatGalleryTag(
  tagSections: readonly GalleryTagSection[],
  linkForm: string
): string {
  for (const section of tagSections) {
    for (const tag of section.tags) {
      if (tag.linkForm === linkForm) {
        return tag.name;
      }
      for (const subtag of tag.subtags ?? []) {
        if (subtag.linkForm === linkForm) {
          return subtag.name;
        }
      }
    }
  }

  if (linkForm === 'top-contributors') {
    return TOP_CONTRIBUTORS_LABEL;
  }

  return linkForm
    .split('-')
    .map(capitalizeFirst)
    .join(' ');
}

export function isOnReportGallery(): boolean {
  return getReportGalleryMatch() != null;
}

export function getReportGalleryMatch(): ReturnType<typeof matchPath> {
  return matchPath(history.location.pathname, {path: GALLERY_PATH});
}

export function getReportGalleryPathParams(
  tagSections: readonly GalleryTagSection[]
): {
  sort: string | null;
  tag: string | null;
} | null {
  const match = getReportGalleryMatch();
  if (match == null) {
    return null;
  }
  if (match.params.tag) {
    return {
      sort: match.params.sortOrTag!,
      tag: match.params.tag,
    };
  }
  if (match.params.sortOrTag) {
    const section = sectionFromTag(tagSections, match.params.sortOrTag);
    if (section) {
      return {
        sort: null,
        tag: match.params.sortOrTag,
      };
    }
    return {
      sort: match.params.sortOrTag,
      tag: null,
    };
  }
  return {sort: null, tag: null};
}

export function useSortOrTagToPath(s: string): string {
  const tagSections = useGalleryTagSections();
  return sortOrTagToPath(tagSections, s);
}

export function sortOrTagToPath(
  tagSections: readonly GalleryTagSection[],
  s: string
): string {
  for (const section of tagSections) {
    for (const tag of section.tags) {
      if (tag.name === s) {
        return tag.linkForm;
      }
      for (const subtag of tag.subtags ?? []) {
        if (subtag.name === s) {
          return subtag.linkForm;
        }
      }
    }
  }
  return s
    .toLowerCase()
    .replace('&', 'and')
    .split(' ')
    .join('-')
    .trim();
}

interface InitGalleryTagSectionsResult {
  loading: boolean;
  tagSections: readonly GalleryTagSection[];
}

export function useInitGalleryTagSections(): InitGalleryTagSectionsResult {
  const dispatch = useDispatch();
  const {loading, data} = useGalleryTagSectionsQuery();
  const tagSections: readonly GalleryTagSection[] = useMemo(
    () =>
      data?.galleryTagSections != null
        ? JSON.parse(data.galleryTagSections)
        : EMPTY_ARRAY,
    [data]
  );

  useEffect(() => {
    if (!loading) {
      dispatch(setGalleryTagSections(tagSections));
    }
  }, [dispatch, loading, tagSections]);

  return {loading, tagSections};
}

export function useGalleryTagSections(): readonly GalleryTagSection[] {
  return useSelector(state => state.global.galleryTagSections ?? EMPTY_ARRAY);
}

export function syncReportTags(
  reports: FeaturedReport[],
  before: string,
  after: string
): FeaturedReport[] {
  return produce(reports, draft => {
    for (const r of draft) {
      for (let i = 0; i < r.tags.length; i++) {
        if (r.tags[i] === before) {
          r.tags[i] = after;
        }
      }
    }
  });
}

export function useShowFreshnessPills(): boolean {
  return useRef(showFreshnessPills()).current;
}

const GALLERY_VISITED_KEY = 'galleryVisited';

// don't show pills on first visit
function showFreshnessPills(): boolean {
  if (localStorage?.getItem(GALLERY_VISITED_KEY) == null) {
    localStorage.setItem(GALLERY_VISITED_KEY, '');
    return false;
  }
  return true;
}
